/**
 * Customer-facing onboarding RPC definitions (CLA-428).
 *
 * The coarse-grained RpcGroup the ClawInstall customer onboarding flow
 * consumes end-to-end. Deliberately ONE group under token/Clerk auth (the
 * standard {@link AuthMiddleware}, NOT `AdminMiddleware`):
 *
 *  - `resolveLink` — exchange a magic-link token for the onboarding context
 *    (customer, selections, deployment, agreed price). Typed
 *    {@link OnboardingLinkError} for invalid/expired/already-resolved links.
 *  - `bindSignup` — after a Clerk sign-up, bind the identity onto the intake's
 *    EXISTING person/customer when the email matches; on a mismatch returns a
 *    needs-confirmation result naming the target customer (no binding).
 *  - `confirmLink` — explicitly bind after a mismatch; idempotent.
 *  - `createEmbeddedCheckoutSession` — open an EMBEDDED Stripe checkout and
 *    return its `client_secret`; first invoice = recurring rate + one-time
 *    setup fee.
 *  - `createAgentFromIntake` — gated on an active subscription, materialise the
 *    first agent from the intake (idempotent; `agent.intake_id` set).
 *  - `updateAgentRegion` — change a cloud-vps agent's region from the Install
 *    step, ONLY before provisioning is dispatched (typed
 *    {@link ProvisioningAlreadyDispatchedError} after); validated against the
 *    shared Hetzner locations.
 *  - `getInstall` — mint the install hand-off (Mac curl one-liner / cloud
 *    provisioning progress handle) for the caller's agent. Cloud dispatch stamps
 *    `provisioning_dispatched_at`, locking the region.
 *  - `agentStatus` — report whether the agent has phoned home (heartbeat), so
 *    the UI can auto-advance.
 *  - `state` — the catalog filtered to the intake's selections once the
 *    intake-seeded agent exists (full catalog otherwise) plus the derived
 *    current step so onboarding resumes where it left off.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { AgentInstallationStatus, DeploymentType } from "@fm/db/models/AgentInstallation";
import { AgentRatePlan, CustomerType } from "@fm/db/models/Customer";
import { Schema } from "effect";
import { AgentsCreateResult, AgentsMintInstallLinkResult } from "../admin/agents.rpc.ts";
import { ProviderCatalogResponse } from "../admin/installation-connections.rpc.ts";
import { InstallTokenCipherError, WorkflowDispatchFailedError } from "../admin/installs.rpc.ts";
import { NodeHealth } from "../admin/nodes.rpc.ts";
import { AuthMiddleware, EnsureUserAuthMiddleware } from "../auth.tags.ts";
import { BrandMiddleware } from "../brand.tags.ts";
import {
  DatabaseError,
  Forbidden,
  OnboardingLinkError,
  ProvisioningAlreadyDispatchedError,
  RecordNotFoundError,
  SubscriptionError,
  Unauthorized,
  ValidationError,
  ValkeyError,
} from "../errors.ts";
import { CommandDispatchError, RenderError, SkillArchiveRefNotFoundError } from "../profile.ts";
import { IntakeInstallMode, SelfServePlatform } from "./self-serve.schemas.ts";

// ============================================================================
// Support contact
// ============================================================================

/**
 * The reseller behind a customer, projected to a customer-facing support
 * contact so surfaces can render "Talk to <name>" / "Provisioned by <name>"
 * with real data. `name` is the reseller customer's display name; `email` is
 * its primary contact person's email (`null` when the reseller has no primary
 * contact person, or that person has no email yet). The whole struct is `null`
 * (never rendered here) when the customer has no reseller — surfaces fall back
 * to ClawInstall support.
 *
 * @since 1.0.0
 */
export const SupportContact = Schema.Struct({
  name: Schema.String,
  email: Schema.NullOr(Schema.String),
});
export type SupportContact = Schema.Schema.Type<typeof SupportContact>;

// ============================================================================
// resolveLink
// ============================================================================

/**
 * Resolve a customer magic link by its plaintext token. The server hashes the
 * token and looks the intake up by `link_token_hash`.
 *
 * @since 1.0.0
 */
export const ResolveLinkInput = Schema.Struct({
  token: Schema.NonEmptyTrimmedString,
});
export type ResolveLinkInput = Schema.Schema.Type<typeof ResolveLinkInput>;

/**
 * The onboarding context behind a resolved magic link — never the plaintext
 * token or any secret.
 *
 * @since 1.0.0
 */
export const OnboardingContext = Schema.Struct({
  /**
   * The id of the intake this magic link resolves to. The landing route
   * compares it against the signed-in caller's active onboarding intake so a
   * FRESH link for a DIFFERENT intake is never silently swallowed by an
   * existing resume (CLA-479).
   */
  intakeId: Schema.String,
  customer: Schema.Struct({
    id: Schema.String,
    name: Schema.String,
    type: CustomerType,
  }),
  /**
   * The intake owner's email, sourced from the intake's primary person. Empty
   * string when the intake has no primary person yet. Used by the Welcome →
   * Account step to PRE-FILL the passwordless sign-up email (CLA-450).
   */
  email: Schema.String,
  selections: Schema.Struct({
    channels: Schema.Array(Schema.String),
    connectors: Schema.Array(Schema.String),
    llm: Schema.NullOr(Schema.String),
  }),
  deploymentType: Schema.NullOr(DeploymentType),
  agreedPrice: Schema.Struct({
    amount: Schema.NullOr(Schema.Int),
    plan: Schema.NullOr(AgentRatePlan),
  }),
  /**
   * The reseller behind this customer, projected to a support contact so the
   * customer surfaces can render "Talk to <name>" with real data. `null` when
   * the customer has no reseller (UI falls back to ClawInstall support).
   */
  supportContact: Schema.NullOr(SupportContact),
  /**
   * The display name of the intake-tied, non-deleted agent, so the Welcome
   * headline can render "Meet <name>." with real data. `null` when no agent is
   * provisioned for the intake yet.
   */
  agentName: Schema.NullOr(Schema.String),
});
export type OnboardingContext = Schema.Schema.Type<typeof OnboardingContext>;

// ============================================================================
// bindSignup / confirmLink
// ============================================================================

/**
 * Bind input — the magic-link token plus the optional Clerk org id to
 * back-fill onto the customer. The Clerk user id is taken from the auth
 * context, never the payload.
 *
 * @since 1.0.0
 */
export const BindSignupInput = Schema.Struct({
  token: Schema.NonEmptyTrimmedString,
  clerkOrgId: Schema.optional(Schema.String),
});
export type BindSignupInput = Schema.Schema.Type<typeof BindSignupInput>;

/**
 * Bind result.
 *
 *  - match → `bound: true`, `needsConfirmation: false`, `customerId`/
 *    `customerName` carry the bound customer.
 *  - mismatch → `bound: false`, `needsConfirmation: true`, `targetCustomerId`/
 *    `targetCustomerName` name the customer the caller would be confirming.
 *
 * @since 1.0.0
 */
export const BindResult = Schema.Struct({
  bound: Schema.Boolean,
  needsConfirmation: Schema.Boolean,
  customerId: Schema.NullOr(Schema.String),
  customerName: Schema.NullOr(Schema.String),
  targetCustomerId: Schema.NullOr(Schema.String),
  targetCustomerName: Schema.NullOr(Schema.String),
});
export type BindResult = Schema.Schema.Type<typeof BindResult>;

// ============================================================================
// createEmbeddedCheckoutSession
// ============================================================================

/**
 * Embedded-checkout input — the front-end `return_url` Stripe redirects to
 * after the embedded form completes.
 *
 * @since 1.0.0
 */
export const CreateEmbeddedCheckoutInput = Schema.Struct({
  returnUrl: Schema.NonEmptyTrimmedString,
});
export type CreateEmbeddedCheckoutInput = Schema.Schema.Type<typeof CreateEmbeddedCheckoutInput>;

/**
 * Embedded-checkout result — the `client_secret` the front-end mounts inline.
 * No redirect URL is returned (that is the whole point of embedded mode).
 *
 * @since 1.0.0
 */
export const EmbeddedCheckoutResult = Schema.Struct({
  clientSecret: Schema.String,
});
export type EmbeddedCheckoutResult = Schema.Schema.Type<typeof EmbeddedCheckoutResult>;

// ============================================================================
// createAgentFromIntake
// ============================================================================

/**
 * Materialise-agent input. `region` is only meaningful for a `cloud-vps`
 * intake — the Install-step override (CLA-484) that takes precedence over the
 * region the CSR captured at intake (`intake.region`), which in turn falls back
 * to the server default. A `local-mac` intake ignores it. Omitted on the
 * idempotent re-call.
 *
 * @since 1.0.0
 */
export const CreateAgentFromIntakeInput = Schema.Struct({
  region: Schema.optional(Schema.String),
});
export type CreateAgentFromIntakeInput = Schema.Schema.Type<typeof CreateAgentFromIntakeInput>;

// ============================================================================
// updateAgentRegion
// ============================================================================

/**
 * Change the region a cloud-vps agent will provision in, from the Install step
 * (CLA-484). Only valid BEFORE provisioning has been dispatched — the region is
 * captured at intake, confirmed/changed once here, then immutable. `region` is a
 * Hetzner location slug validated server-side against the shared
 * `HETZNER_LOCATIONS` set. The agent must belong to a customer the caller is a
 * member of.
 *
 * @since 1.0.0
 */
export const UpdateAgentRegionInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
  region: Schema.NonEmptyTrimmedString,
});
export type UpdateAgentRegionInput = Schema.Schema.Type<typeof UpdateAgentRegionInput>;

/**
 * Result of a successful region change — the agent id and the newly-persisted
 * region, so the Install step can reflect it without a refetch.
 *
 * @since 1.0.0
 */
export const UpdateAgentRegionResult = Schema.Struct({
  agentId: Schema.String,
  region: Schema.String,
});
export type UpdateAgentRegionResult = Schema.Schema.Type<typeof UpdateAgentRegionResult>;

// ============================================================================
// getInstall / agentStatus
// ============================================================================

/**
 * Single-agent input for `getInstall` / `agentStatus`. The agent must belong
 * to a customer the caller is a member of.
 *
 * @since 1.0.0
 */
export const OnboardingAgentInput = Schema.Struct({
  agentId: Schema.NonEmptyTrimmedString,
});
export type OnboardingAgentInput = Schema.Schema.Type<typeof OnboardingAgentInput>;

/**
 * Agent heartbeat projection — `phonedHome` flips true once the agent has
 * reported at least one heartbeat; `health` is the same online/offline/unknown
 * verdict the fleet uses.
 *
 * @since 1.0.0
 */
export const AgentStatusResult = Schema.Struct({
  agentId: Schema.String,
  phonedHome: Schema.Boolean,
  lastSeenAt: Schema.NullOr(Schema.DateTimeUtc),
  health: NodeHealth,
  status: AgentInstallationStatus,
});
export type AgentStatusResult = Schema.Schema.Type<typeof AgentStatusResult>;

// ============================================================================
// activate
// ============================================================================

/**
 * Result of a successful `onboarding/activate` — the ownership-checked
 * customer-safe publish. `publishVersion` is the version of the newly-inserted
 * `installation_profile_publishes` row (the connections/profile snapshot pushed
 * to the box), `commandId` the enqueued `applyProfile` command's id so the UI
 * can correlate the box-side apply.
 *
 * @since 1.0.0
 */
export const OnboardingActivateResult = Schema.Struct({
  publishVersion: Schema.Int,
  commandId: Schema.String,
});
export type OnboardingActivateResult = Schema.Schema.Type<typeof OnboardingActivateResult>;

// ============================================================================
// state
// ============================================================================

/**
 * The derived onboarding step so the wizard resumes where the customer left
 * off:
 *
 *  - `checkout` — no active subscription yet.
 *  - `create_agent` — subscription active but the intake agent is not
 *    materialised.
 *  - `install` — agent exists but has not phoned home.
 *  - `connect` — agent is live; channels/connectors remain.
 *
 * @since 1.0.0
 */
export const OnboardingStep = Schema.Literal("checkout", "create_agent", "install", "connect");
export type OnboardingStep = Schema.Schema.Type<typeof OnboardingStep>;

/**
 * Payload for `onboarding/beginSelfServe` — the self-serve wizard's entry
 * point, taken by a signed-in customer who arrived WITHOUT a CSR-minted magic
 * link. `mode` picks the two shapes self-serve supports:
 *
 *  - `existing` — adopt a machine the customer already runs (the installer
 *    targets `platform`).
 *  - `cloud` — provision a fresh cloud agent; `platform` is irrelevant.
 *
 * `customerName` names the customer record when one must be created; omitted
 * when the caller already has one.
 *
 * @since 1.0.0
 */
export const OnboardingBeginSelfServeInput = Schema.Struct({
  mode: Schema.Literal("existing", "cloud"),
  platform: Schema.optional(SelfServePlatform),
  customerName: Schema.optional(Schema.NonEmptyTrimmedString),
});
export type OnboardingBeginSelfServeInput = Schema.Schema.Type<typeof OnboardingBeginSelfServeInput>;

/**
 * Result of `onboarding/beginSelfServe`: the customer and intake the wizard now
 * owns, plus the {@link OnboardingStep} the client should land on.
 *
 * @since 1.0.0
 */
export const OnboardingBeginSelfServeResult = Schema.Struct({
  customerId: Schema.String,
  intakeId: Schema.String,
  step: OnboardingStep,
});
export type OnboardingBeginSelfServeResult = Schema.Schema.Type<typeof OnboardingBeginSelfServeResult>;

/**
 * The combined-invoice price breakdown the Billing step renders inline — the
 * FIRST time the customer sees the price. The first checkout invoice charges
 * `setupFeeAmount` (one-time) plus `recurringAmount` (the agent rate at
 * `recurringPlan` cadence); every later invoice bills the recurring amount
 * only. All amounts are integer cents. `null` when the customer has no agreed
 * agent rate yet — checkout would fail anyway, so the UI shows a calm notice.
 *
 * @since 1.0.0
 */
export const PriceBreakdown = Schema.Struct({
  setupFeeAmount: Schema.Int,
  recurringAmount: Schema.Int,
  recurringPlan: AgentRatePlan,
});
export type PriceBreakdown = Schema.Schema.Type<typeof PriceBreakdown>;

/**
 * Compact projection of the intake-seeded agent — just enough for the Install
 * step to mint the install hand-off (`id` feeds `getInstall` / `agentStatus`)
 * and label the cloud branch (`region`). `null` until the agent is
 * materialised.
 *
 * @since 1.0.0
 */
export const OnboardingAgent = Schema.Struct({
  id: Schema.String,
  region: Schema.String,
  /**
   * Whether the cloud-vps provisioning workflow has already been dispatched for
   * this agent (`agent_installations.provisioning_dispatched_at` set). The
   * Install step shows the region as customer-changeable while this is `false`
   * and renders it read-only once `true` — provisioning is dispatched only on
   * explicit confirm, and the region is immutable after (CLA-484). Always
   * `false` for a `local-mac` agent (it never provisions a cloud box).
   */
  provisioningDispatched: Schema.Boolean,
});
export type OnboardingAgent = Schema.Schema.Type<typeof OnboardingAgent>;

/**
 * The onboarding state — the connector/channel catalog (filtered to the
 * intake's selections once the intake-seeded agent exists, full otherwise),
 * the derived current step, and the combined-invoice price breakdown the
 * Billing step shows inline.
 *
 * @since 1.0.0
 */
export const OnboardingState = Schema.Struct({
  /**
   * The id of the intake this state is derived from. Surfaced so the magic-link
   * landing route can reconcile a FRESH link's intake against the signed-in
   * caller's active one — a mismatch must route the new intake's Welcome rather
   * than resume the old flow (CLA-479).
   */
  intakeId: Schema.String,
  step: OnboardingStep,
  catalog: ProviderCatalogResponse,
  deploymentType: Schema.NullOr(DeploymentType),
  hasAgent: Schema.Boolean,
  agent: Schema.NullOr(OnboardingAgent),
  subscriptionActive: Schema.Boolean,
  /**
   * Whether this customer is COMPED — has no agreed agent rate
   * (`customers.agent_rate_amount IS NULL`). Comped customers (the common
   * CLI-allocated case) skip the Billing step entirely: `subscriptionActive` is
   * forced `true` and `priceBreakdown` is `null`, and the Billing route redirects
   * a comped customer straight to Connect rather than ever rendering a checkout
   * or the "couldn't load pricing" panel. `false` for a rate-bearing (paying)
   * customer, who sees Billing unchanged.
   */
  comped: Schema.Boolean,
  /**
   * Whether this intake was started by the customer themselves through the
   * self-serve wizard (`intakes.self_serve_at IS NOT NULL`) rather than minted
   * by a CSR. `installMode` is how that self-serve customer chose to get an
   * agent — `install` (fresh machine) or `adopt` (a box they already run) — and
   * is `null` on every CSR-mediated intake.
   */
  selfServe: Schema.Boolean,
  installMode: Schema.NullOr(IntakeInstallMode),
  priceBreakdown: Schema.NullOr(PriceBreakdown),
  /**
   * The reseller behind this customer, projected to a support contact so the
   * Agent Home surfaces can render "Provisioned by <name>" with real data.
   * `null` when the customer has no reseller (UI falls back to ClawInstall
   * support).
   */
  supportContact: Schema.NullOr(SupportContact),
});
export type OnboardingState = Schema.Schema.Type<typeof OnboardingState>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Customer-facing onboarding RPC group. Every endpoint requires a valid
 * token/Clerk session via {@link AuthMiddleware} (NOT `AdminMiddleware`). Tags
 * are namespaced under `onboarding/`.
 *
 * @since 1.0.0
 */
export class OnboardingRpcs extends RpcGroup.make(
  // resolveLink is TOKEN-gated, NOT session-gated: the magic-link token is the
  // credential, so the Welcome screen resolves the customer/selections/email for
  // an anonymous recipient BEFORE Clerk sign-up (CLA-450). Deliberately no
  // `AuthMiddleware` — the handler never reads `CurrentActor`.
  Rpc.make("onboarding/resolveLink", {
    payload: ResolveLinkInput,
    success: OnboardingContext,
    error: Schema.Union(OnboardingLinkError, RecordNotFoundError, DatabaseError),
  }),
  // bindSignup / confirmLink run for a JUST-signed-up Clerk user who has no
  // person row yet, so they use {@link EnsureUserAuthMiddleware} (NOT the plain
  // {@link AuthMiddleware}, which would 401 a fresh sign-up). The middleware
  // claims the intake's unbound same-email person — or creates an enriched one
  // — before the handler runs, so the bind always has a person to work with
  // (CLA-481).
  Rpc.make("onboarding/bindSignup", {
    payload: BindSignupInput,
    success: BindResult,
    error: Schema.Union(Unauthorized, OnboardingLinkError, RecordNotFoundError, DatabaseError),
  }).middleware(EnsureUserAuthMiddleware),
  Rpc.make("onboarding/confirmLink", {
    payload: BindSignupInput,
    success: BindResult,
    error: Schema.Union(Unauthorized, OnboardingLinkError, RecordNotFoundError, DatabaseError),
  }).middleware(EnsureUserAuthMiddleware),
  // beginSelfServe runs for a signed-in customer who arrived with NO magic
  // link, so it shares bindSignup's {@link EnsureUserAuthMiddleware}: the
  // person row may not exist yet at the moment the wizard starts.
  Rpc.make("onboarding/beginSelfServe", {
    payload: OnboardingBeginSelfServeInput,
    success: OnboardingBeginSelfServeResult,
    error: Schema.Union(Unauthorized, ValidationError, RecordNotFoundError, DatabaseError),
  }).middleware(EnsureUserAuthMiddleware),
  Rpc.make("onboarding/createEmbeddedCheckoutSession", {
    payload: CreateEmbeddedCheckoutInput,
    success: EmbeddedCheckoutResult,
    error: Schema.Union(Unauthorized, ValidationError, SubscriptionError, RecordNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware).middleware(BrandMiddleware),
  Rpc.make("onboarding/createAgentFromIntake", {
    payload: CreateAgentFromIntakeInput,
    success: AgentsCreateResult,
    error: Schema.Union(
      Unauthorized,
      ValidationError,
      SubscriptionError,
      RecordNotFoundError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("onboarding/updateAgentRegion", {
    payload: UpdateAgentRegionInput,
    success: UpdateAgentRegionResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      ValidationError,
      ProvisioningAlreadyDispatchedError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("onboarding/getInstall", {
    payload: OnboardingAgentInput,
    success: AgentsMintInstallLinkResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      InstallTokenCipherError,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("onboarding/agentStatus", {
    payload: OnboardingAgentInput,
    success: AgentStatusResult,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(AuthMiddleware),
  // activate is the customer-safe publish surface (the redesign's "Activate
  // Mara" button): it enforces agent ownership before publishing the
  // connections/profile snapshot to the box and best-effort advances the
  // customer's intake toward `completed`. The unguarded `ProfileRpcs.publish`
  // must NEVER be called from the customer UI — this is its ownership-checked
  // replacement.
  Rpc.make("onboarding/activate", {
    payload: OnboardingAgentInput,
    success: OnboardingActivateResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      RenderError,
      CommandDispatchError,
      SkillArchiveRefNotFoundError,
    ),
  }).middleware(AuthMiddleware),
  // state can be polled immediately after sign-up (before bind completes), so
  // it too guarantees a person via {@link EnsureUserAuthMiddleware} rather than
  // 401-ing a fresh sign-up (CLA-481).
  Rpc.make("onboarding/state", {
    success: OnboardingState,
    error: Schema.Union(Unauthorized, RecordNotFoundError, DatabaseError, ValkeyError),
  }).middleware(EnsureUserAuthMiddleware),
) {}
